import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingController, ToastController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { BaseClass } from 'src/app/shared/classes/base';
import { AuthService } from 'src/app/shared/services/auth.service';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { UtilService } from "../../shared/services/util.service";
import { GoalStatusEnum } from '../_interfaces/goal.interface';
import { SubordinateGoalPreview } from '../_interfaces/subordinate-goals-preview.interface';
import { SubordinateGoalStatus } from '../_interfaces/subordinate-goals-status.enum';
import { GoalsService } from '../goals.service';

@Component({
    selector: 'app-goals-validation',
    templateUrl: './goals-validation.page.html'
})
export class GoalsValidationPage extends BaseClass implements OnDestroy {

    public goals: Array<SubordinateGoalPreview>;
    public consentAttributes: Array<any>;
    public displayedGoals: Array<SubordinateGoalPreview>;
    protected readonly GoalStatusEnum = GoalStatusEnum;
    public isAllowedToReopen: boolean = false;
    public hasConsentAttributes: boolean = false;

    private _userRoles: Array<string> = [];
    private _cycleId: number;
    private _searchSubject: Subject<string> = new Subject();
    private _processId: number;
    public forcedManagerInclusion: boolean = false;

    constructor(
            protected _toastController: ToastController,
            protected _translationService: TranslationService,
            private _loadingController: LoadingController,
            private _router: Router,
            private _authService: AuthService,
            private _goalsService: GoalsService,
            private _activatedRoute: ActivatedRoute,
            private _utilService: UtilService,
    ) {
        super(_toastController, _translationService);

        const state = this._router.getCurrentNavigation().extras.state;
        this._processId = state
                ? parseInt(state.processId)
                : null;
        this.forcedManagerInclusion = state?.forcedManagerInclusion ? true : false;

        this._utilService.insertProcessIdInLocalStorage(this._processId);

        this._cycleId = parseInt(
                this._activatedRoute.snapshot.paramMap.get('cycleId'), 10
        );
        this._userRoles = this._authService.getUserRoles();
        if (this._userRoles.includes('Administrator') ||
                this._userRoles.includes('HR')) {
            this.isAllowedToReopen = true;
        }
        this._setSearchSubscription();
    }

    ionViewWillEnter() {
        this._loadSubordinates(this._cycleId);
    }

    public goBack(): void {
        this._utilService.removeProcessIdFromLocalStorage();
        this._router.navigate(['dashboard']);
    }

    ionViewDidLeave() {
        this._utilService.removeProcessIdFromLocalStorage();
    }

    public goToValidation(goal: SubordinateGoalPreview): void {
        this._router.navigate(['goals-validation/' + this._cycleId + '/' + goal.id]);
    }

    public getStatusColor(status: SubordinateGoalStatus): string {
        switch (status) {
            case SubordinateGoalStatus.Validated:
                return 'success';
            case SubordinateGoalStatus.WaitingChanges:
                return 'warning';
            case SubordinateGoalStatus.Pending:
            case SubordinateGoalStatus.WaitingValidation:
            default:
                return 'primary';
        }
    }

    public updateSearch(searchTextValue: string) {
        this._searchSubject.next(searchTextValue);
    }

    private async _loadSubordinates(cycleId: number): Promise<void> {
        const loading = await this._loadingController.create();
        await loading.present();

        this._goalsService.getSubordinatesForValidation(
                cycleId
        ).subscribe((response) => {
            this.goals = response.data;
            this.displayedGoals = response.data;
            this._loadConsentAttributes(this._cycleId);
            loading.dismiss();

        }, (error) => {
            loading.dismiss();
            this.notify(this.getErrorNotification(error), 'danger');
        });
    }

    private async _loadConsentAttributes(cycleId: number): Promise<void> {
        const loading = await this._loadingController.create();
        await loading.present();

        this._goalsService.getConsentAttributes(
                cycleId
        ).subscribe((response) => {
            this.consentAttributes = response.data;
            if (this.consentAttributes.length > 0) {
                this.hasConsentAttributes = true;
                const isAttributeOnConsent = this.consentAttributes.map((attribute) => {
                    return attribute.id;
                });
                this.displayedGoals.forEach((goal) => {
                    const userAttributes = goal.userAttributes;

                    const isAllAttributesOnUser = isAttributeOnConsent.every(attribute => {
                        return userAttributes.some(userAttributes => userAttributes.id === attribute);
                    });
                    if (isAllAttributesOnUser) goal.hasConsentAttributes = true;
                })
            }
            loading.dismiss();

        }, (error) => {
            loading.dismiss();
            this.notify(this.getErrorNotification(error), 'danger');
        });
    }

    private _setSearchSubscription() {
        this._searchSubject.pipe(
                distinctUntilChanged(),
                debounceTime(1000)
        ).subscribe((searchValue: string) => {
            this.displayedGoals = this.goals.filter(goal => {
                return goal.name.toLowerCase().includes(
                        searchValue.toLowerCase().trim()
                );
            });
        });
    }

    public reopenValidation(goal: SubordinateGoalPreview): void {
        this._goalsService.reopenGoalsValidationForUser(
                this._cycleId, goal.id
        ).subscribe((response) => {
                    this._loadSubordinates(this._cycleId);
                    this.notifySuccess('performanceCycles.processReopened');
                }
                , (error) => this.notifyError(error));
    }

    ngOnDestroy() {
        this._searchSubject.unsubscribe();
    }

    protected readonly SubordinateGoalStatus = SubordinateGoalStatus;
}
